/*
 * carousel-arrows.js — global, one-instance arrow controller for any
 * horizontal scroll-snap carousel that uses the ui-carousel-arrows snippet.
 *
 * Pattern mirrors product-card-ajax.js (Step 23.5): a single document-level
 * click delegate handles every arrow click on the page. Per-scroller scroll
 * state is bound lazily via IntersectionObserver — listeners only attach
 * when a carousel actually enters the viewport, avoiding layout work on
 * sections the user never reaches.
 */

const SCROLL_FRACTION = 0.8;
const EDGE_THRESHOLD = 8;

document.addEventListener('click', (event) => {
  const arrow = event.target.closest('.ui-carousel-arrow');
  if (!arrow) return;

  const selector = arrow.dataset.target;
  if (!selector) return;

  const scroller = document.querySelector(selector);
  if (!scroller) return;

  const direction = arrow.classList.contains('ui-carousel-arrow--prev') ? -1 : 1;
  scroller.scrollBy({
    left: direction * scroller.clientWidth * SCROLL_FRACTION,
    behavior: 'smooth',
  });
});

const syncEdgeState = (scroller) => {
  const container = scroller.dataset.arrowsContainer
    ? document.querySelector(scroller.dataset.arrowsContainer)
    : findArrowsContainer(scroller);
  if (!container) return;

  const atStart = scroller.scrollLeft <= EDGE_THRESHOLD;
  const atEnd =
    scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth - EDGE_THRESHOLD;

  container.toggleAttribute('data-at-start', atStart);
  container.toggleAttribute('data-at-end', atEnd);
};

const findArrowsContainer = (scroller) => {
  const selector = `[data-target="#${CSS.escape(scroller.id)}"]`;
  const arrow = document.querySelector(selector);
  return arrow ? arrow.closest('[data-carousel-arrows-container]') : null;
};

const bound = new WeakSet();

const bindScroller = (scroller) => {
  if (bound.has(scroller)) return;
  bound.add(scroller);

  syncEdgeState(scroller);
  scroller.addEventListener('scroll', () => syncEdgeState(scroller), { passive: true });

  const ro = new ResizeObserver(() => syncEdgeState(scroller));
  ro.observe(scroller);
};

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        bindScroller(entry.target);
        observer.unobserve(entry.target);
      }
    }
  },
  { rootMargin: '200px' }
);

const observeAll = () => {
  document.querySelectorAll('[data-carousel-scroller]').forEach((scroller) => {
    if (!bound.has(scroller)) observer.observe(scroller);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeAll);
} else {
  observeAll();
}

document.addEventListener('shopify:section:load', observeAll);
